import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Maximize2 } from "lucide-react";
import { api, resolveAssetUrl } from "@/lib/api";

// ---- Lightbox: scrolls through every photo across every album ----
function Lightbox({ items, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex || 0);
  const count = items.length;
  const next = useCallback(() => setIdx(i => (i + 1) % count), [count]);
  const prev = useCallback(() => setIdx(i => (i - 1 + count) % count), [count]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [next, prev, onClose]);

  const current = items[idx];
  if (!current) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center" data-testid="gallery-lightbox">
      <button onClick={onClose} aria-label="Close" data-testid="lightbox-close"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-[110] h-12 w-12 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
        <X className="h-6 w-6"/>
      </button>
      <button onClick={prev} aria-label="Previous" data-testid="lightbox-prev"
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-[110] h-12 w-12 sm:h-14 sm:w-14 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
        <ChevronLeft className="h-7 w-7"/>
      </button>
      <button onClick={next} aria-label="Next" data-testid="lightbox-next"
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-[110] h-12 w-12 sm:h-14 sm:w-14 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
        <ChevronRight className="h-7 w-7"/>
      </button>
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-[110] text-white">
        <div className="text-[10px] uppercase tracking-[0.22em] text-saffron font-bold">{current.albumTitle}</div>
        <div className="text-xs text-white/70 mt-1">{idx + 1} / {count}</div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={current.id || idx}
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }} transition={{ duration: 0.25 }}
          className="relative max-w-[92vw] max-h-[82vh] flex flex-col items-center justify-center">
          <img src={resolveAssetUrl(current.image)} alt={current.caption || "photo"} className="max-w-[92vw] max-h-[78vh] object-contain rounded-xl shadow-2xl" data-testid="lightbox-img"/>
          {current.caption && (
            <div className="mt-4 text-center text-white/90 text-sm sm:text-base font-medium max-w-2xl">{current.caption}</div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ---- Album section ----
function Album({ album, onOpen, baseIndex }) {
  const photos = album.photos || [];
  if (photos.length === 0) return null;
  return (
    <section className="mt-16 first:mt-0" data-testid={`album-${album.id}`}>
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <span className="overline">{photos.length} photo{photos.length !== 1 ? "s" : ""}</span>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-navy mt-1">{album.title}</h2>
          {album.description && <p className="text-sm text-ink-muted mt-2 max-w-2xl">{album.description}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {photos.map((ph, i) => (
          <motion.button key={ph.id || i}
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.45, delay: (i % 8) * 0.05 }}
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => onOpen(baseIndex + i)}
            data-testid={`album-${album.id}-photo-${i}`}
            className="group relative aspect-square rounded-2xl overflow-hidden bg-slate-100 shadow-md hover:shadow-xl transition-shadow">
            <img src={resolveAssetUrl(ph.image)} alt={ph.caption || `${album.title} ${i+1}`}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
            <div className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full bg-white/90 text-navy opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <Maximize2 className="h-4 w-4"/>
            </div>
            {ph.caption && (
              <div className="absolute bottom-0 left-0 right-0 p-3 text-left text-white text-xs sm:text-sm font-medium translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                {ph.caption}
              </div>
            )}
          </motion.button>
        ))}
      </div>
    </section>
  );
}

export default function GalleryPage() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    api.get("/galleries").then(r => setAlbums(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Flatten all photos across all albums into one big list (for lightbox navigation)
  const flat = useMemo(() => {
    const out = [];
    for (const a of albums) {
      for (const p of (a.photos || [])) {
        out.push({ ...p, albumTitle: a.title });
      }
    }
    return out;
  }, [albums]);

  // Compute baseIndex per album so each thumbnail's click maps to the right flat index
  const albumBases = useMemo(() => {
    const bases = {};
    let i = 0;
    for (const a of albums) { bases[a.id] = i; i += (a.photos || []).length; }
    return bases;
  }, [albums]);

  return (
    <>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="overline">Gallery</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 text-navy">Moments from the TARA family.</h1>
          <p className="mt-5 text-lg text-ink-muted leading-relaxed max-w-3xl">
            Photos from our offices, events, partner meets and customer success stories. Tap any photo to view it full-size.
          </p>
        </motion.div>
      </section>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-24">
        {loading ? (
          <div className="text-center text-ink-muted py-20">Loading albums…</div>
        ) : albums.length === 0 ? (
          <div className="text-center py-20" data-testid="gallery-empty">
            <div className="overline">No albums yet</div>
            <div className="mt-2 text-ink-muted">Check back soon — we're putting together our first gallery.</div>
          </div>
        ) : (
          albums.map((a) => <Album key={a.id} album={a} onOpen={(i) => setLightboxIndex(i)} baseIndex={albumBases[a.id] || 0}/>)
        )}
      </section>
      <AnimatePresence>
        {lightboxIndex !== null && flat.length > 0 && (
          <Lightbox items={flat} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)}/>
        )}
      </AnimatePresence>
    </>
  );
}
